import { ReactNode } from "react";

import { css } from "@compiled/react";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives/responsive";

import JswInteractiveBoard from "@/components/product-demos/jsw-interactive-board/src";
import {
    IndividualTabPropsQuote,
    DisplayProps,
    renderTabContent
} from "@/components/wpl/tab-group/src";
import TabsQuote from "@/patterns/wpl/tabs-quote/src";

import {
    getAutomateTabsSectionImage,
    getPlanTabsSectionImage,
    getPlanTabsSectionVideo,
    getReleaseTabsSectionImage,
    getReportTabsSectionImage,
    getTrackTabsSectionImage
} from "../localized-images";

const TabsSection = () => {
    const intl = useIntl();

    const QUOTE_IMAGE_PROPS = {
        className: "tab-quote-image",
        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
        alt: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabs.quoteAlt",
            defaultMessage: "Quotation mark"
        })
    };

    const planLocalizedImageSrc = getPlanTabsSectionImage(intl.locale);
    const planLocalizedVideoSrc = getPlanTabsSectionVideo(intl.locale);
    const trackLocalizedImageSrc = getTrackTabsSectionImage(intl.locale);
    const releaseLocalizedImageSrc = getReleaseTabsSectionImage(intl.locale);
    const reportLocalizedImageSrc = getReportTabsSectionImage(intl.locale);
    const automateLocalizedImageSrc = getAutomateTabsSectionImage(intl.locale);

    const getPlanLocalizedMedia = (locale: string): DisplayProps => {
        // english should show a still image, other locales will show a localized gif (since the still has not been localized)
        if (locale === "en") {
            return {
                imageProps: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:49c215de-3b05-40f8-96e2-6dc65c70caa6/Board.png?cdnVersion=1187",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsPlan.imageAltText",
                        defaultMessage: "" // TODO - still awaiting what this should be
                    }),
                    aspectRatio: "none"
                }
            };
        }
        return {
            videoProps: {
                videoLink: planLocalizedVideoSrc,
                videoPreviewImage: planLocalizedImageSrc,
                aspectRatio: "none"
            }
        };
    };

    const planLocalizedMedia = getPlanLocalizedMedia(intl.locale);

    const tab: IndividualTabPropsQuote = {
        textLabel: "Plan",
        id: "tab-plan",
        headerText: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabsHeader.plan",
            defaultMessage: "Plan"
        }),
        bodyText: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabsBody.plan",
            defaultMessage:
                "Break the big ideas down into manageable chunks across teams with user stories, issues, and tasks."
        }),
        quoteImageProps: QUOTE_IMAGE_PROPS,
        quote: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabsQuote.plan",
            defaultMessage:
                "Work becomes a lot more visible when it's all in one place. It makes collaboration a whole lot easier."
        }),
        name: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabsName.plan",
            defaultMessage: "Jeff Lai"
        }),
        company: (
            <FormattedMessage
                id="JiraSoftwarePageTemplate.tabsCompany.plan"
                defaultMessage="Internal Infrastructure, canva"
                values={DEFAULT_VALUES}
            />
        ),
        display: planLocalizedMedia
    };

    const interactiveBoardProps = {
        messages: {
            avatarGroupAriaLabel: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.avatarGroupAriaLabel",
                defaultMessage:
                    "Collection of user avatars showing team members"
            }),
            create: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.create",
                defaultMessage: "Create"
            }),
            createIssueModalAssigneeAssetLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeAssetLabel1",
                defaultMessage: "Assignee: Alvin"
            }),
            createIssueModalAssigneeAssetLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeAssetLabel2",
                defaultMessage: "Assignee: Jane"
            }),
            createIssueModalAssigneeAssetLabel3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeAssetLabel3",
                defaultMessage: "Assignee: Blake"
            }),
            createIssueModalAssigneeOption1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeOption1",
                defaultMessage: "Alvin"
            }),
            createIssueModalAssigneeOption2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeOption2",
                defaultMessage: "Jane"
            }),
            createIssueModalAssigneeOption3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalAssigneeOption3",
                defaultMessage: "Blake"
            }),
            createIssueModalCloseBtn: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalCloseBtn",
                defaultMessage: "Close modal"
            }),
            createIssueModalEpicLinkAssetLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkAssetLabel1",
                defaultMessage: "Epic link: Features"
            }),
            createIssueModalEpicLinkAssetLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkAssetLabel2",
                defaultMessage: "Epic link: Analytics"
            }),
            createIssueModalEpicLinkAssetLabel3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkAssetLabel3",
                defaultMessage: "Epic link: Payments"
            }),
            createIssueModalEpicLinkOption1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption1",
                defaultMessage: "Features"
            }),
            createIssueModalEpicLinkOption1Description: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption1Description",
                defaultMessage: "APP-153"
            }),
            createIssueModalEpicLinkOption2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption2",
                defaultMessage: "Analytics"
            }),
            createIssueModalEpicLinkOption2Description: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption2Description",
                defaultMessage: "APP-154"
            }),
            createIssueModalEpicLinkOption3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption3",
                defaultMessage: "Payments"
            }),
            createIssueModalEpicLinkOption3Description: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalEpicLinkOption3Description",
                defaultMessage: "APP-155"
            }),
            createIssueModalHeader: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalHeader",
                defaultMessage: "Create issue"
            }),
            createIssueModalInput1Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput1Label",
                defaultMessage: "Project"
            }),
            createIssueModalInput2Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput2Label",
                defaultMessage: "Issue type"
            }),
            createIssueModalInput3Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput3Label",
                defaultMessage: "Summary"
            }),
            createIssueModalInput3Placeholder: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput3Placeholder",
                defaultMessage: "Ex: Create a requirements doc"
            }),
            createIssueModalInput4Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput4Label",
                defaultMessage: "Issue description"
            }),
            createIssueModalInput4Placeholder: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput4Placeholder",
                defaultMessage:
                    "Add notes: \nWrite details here like receive final prototypes from design. \n \nAdd tasks: \nWrite necessary tasks to complete your project here."
            }),
            createIssueModalInput5Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput5Label",
                defaultMessage: "Priority"
            }),
            createIssueModalInput6Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput6Label",
                defaultMessage: "Epic link"
            }),
            createIssueModalInput7Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput7Label",
                defaultMessage: "Assignee"
            }),
            createIssueModalInput8Label: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalInput8Label",
                defaultMessage: "Due date"
            }),
            createIssueModalIssueTypeAssetLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeAssetLabel1",
                defaultMessage: "Issue type: Task"
            }),
            createIssueModalIssueTypeAssetLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeAssetLabel2",
                defaultMessage: "Issue type: Story"
            }),
            createIssueModalIssueTypeAssetLabel3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeAssetLabel3",
                defaultMessage: "Issue type: Bug"
            }),
            createIssueModalIssueTypeOption1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeOption1",
                defaultMessage: "Task"
            }),
            createIssueModalIssueTypeOption2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeOption2",
                defaultMessage: "Story"
            }),
            createIssueModalIssueTypeOption3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalIssueTypeOption3",
                defaultMessage: "Bug"
            }),
            createIssueModalPriorityAssetLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityAssetLabel1",
                defaultMessage: "Priority: High"
            }),
            createIssueModalPriorityAssetLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityAssetLabel2",
                defaultMessage: "Priority: Critical"
            }),
            createIssueModalPriorityAssetLabel3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityAssetLabel3",
                defaultMessage: "Priority: Blocker"
            }),
            createIssueModalPriorityAssetLabel4: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityAssetLabel4",
                defaultMessage: "Priority: Minor"
            }),
            createIssueModalPriorityOption1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityOption1",
                defaultMessage: "High"
            }),
            createIssueModalPriorityOption2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityOption2",
                defaultMessage: "Critical"
            }),
            createIssueModalPriorityOption3: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityOption3",
                defaultMessage: "Blocker"
            }),
            createIssueModalPriorityOption4: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalPriorityOption4",
                defaultMessage: "Minor"
            }),
            createIssueModalProjectAssetLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalProjectAssetLabel1",
                defaultMessage: "project team rocket"
            }),
            createIssueModalProjectOption1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalProjectOption1",
                defaultMessage: "Team Rocket"
            }),
            createIssueModalSubmitBtn: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.createIssueModalSubmitBtn",
                defaultMessage: "Create"
            }),
            done: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.done",
                defaultMessage: "Done"
            }),
            doneStaticLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.doneStaticLabel1",
                defaultMessage: "Improve payment checkout time on mobile"
            }),
            doneStaticLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.doneStaticLabel2",
                defaultMessage: "Define requirements to use new AI integrations"
            }),
            inProgStaticLabel1: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.inProgStaticLabel1",
                defaultMessage:
                    "Create AI-generated shopping suggestions for homepage"
            }),
            inProgStaticLabel2: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.inProgStaticLabel2",
                defaultMessage: "Add advanced analytics tracking events"
            }),
            inProgress: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.inProgress",
                defaultMessage: "In Progress"
            }),
            restartDemo: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.restartDemo",
                defaultMessage: "Restart demo"
            }),
            spotlightAddDescriptionDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightAddDescriptionDescription",
                defaultMessage:
                    "Add any important information about the work that’s needed to move it forward."
            }),
            spotlightAddDescriptionHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightAddDescriptionHeading",
                defaultMessage: "Provide useful details"
            }),
            spotlightInitialCreateButtonDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightInitialCreateButtonDescription",
                defaultMessage:
                    "Kanban boards are great for project management. Select create to start tracking work that your team would like to do."
            }),
            spotlightInitialCreateButtonHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightInitialCreateButtonHeading",
                defaultMessage: "Let's set up your board"
            }),
            spotlightMoveIssueDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightMoveIssueDescription",
                defaultMessage:
                    "Now that you’ve added all the info to your task. Select create to see your work on the “To-do” list."
            }),
            spotlightMoveIssueHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightMoveIssueHeading",
                defaultMessage: "Move your task to the board"
            }),
            spotlightSelectEpicLinkDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectEpicLinkDescription",
                defaultMessage:
                    "You can connect this issue to longer term projects. This will help your team plan accurately."
            }),
            spotlightSelectEpicLinkHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectEpicLinkHeading",
                defaultMessage: "Connect work to larger projects"
            }),
            spotlightSelectIssueDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectIssueDescription",
                defaultMessage:
                    "Issues are used to track work in Jira. Tasks, stories, and bugs are different types of issues. Select what type of issue you’d like to create."
            }),
            spotlightSelectIssueHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectIssueHeading",
                defaultMessage: "Create an issue"
            }),
            spotlightSelectPriorityDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectPriorityDescription",
                defaultMessage:
                    "Choose the importance of an issue to change how it appears on the board. Select a level of priority to identify this issue at a glance."
            }),
            spotlightSelectPriorityHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSelectPriorityHeading",
                defaultMessage: "Prioritize your issue"
            }),
            spotlightSummaryDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSummaryDescription",
                defaultMessage:
                    "Summarize your to-do in a few words. It can be anything, like complete mobile app designs or developing a new feature."
            }),
            spotlightSummaryHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.spotlightSummaryHeading",
                defaultMessage: "Tell us about your work"
            }),
            successModalCtaButton: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.successModalCtaButton",
                defaultMessage: "Get it free"
            }),
            successModalDescription: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.successModalDescription",
                defaultMessage:
                    "You’ve taken the first step towards streamlined collaboration and task tracking. Get started for free to manage projects with your team in Jira."
            }),
            successModalHeading: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.successModalHeading",
                defaultMessage: "Congrats, you’ve made your first issue!"
            }),
            toDo: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.toDo",
                defaultMessage: "To Do"
            }),
            todoStaticLabel: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.jswInteractiveBoard.todoStaticLabel",
                defaultMessage: "Design AI shopping suggestions for homepage"
            })
        }
    };

    interface PlanTabHeaderPropTypes {
        headerTextA: string;
        headerTextB: string;
        lozengeText: string;
    }

    const PlanTabHeader = ({
        headerTextA,
        headerTextB,
        lozengeText
    }: PlanTabHeaderPropTypes) => (
        <span className="flex-wrapper">
            <span className="flex-wrapper lozenge-wrapper">
                <Lozenge appearance="success" isBold>
                    {lozengeText}
                </Lozenge>
            </span>
            <span className="hide-below-lg">{headerTextA}</span>
            <span className="hide-above-lg">{headerTextB}</span>
        </span>
    );

    interface PlanTabBodyPropTypes {
        bodyTextA: ReactNode;
        bodyTextB: ReactNode;
    }

    const PlanTabBody = ({ bodyTextA, bodyTextB }: PlanTabBodyPropTypes) => (
        <span>
            <span className="hide-below-lg">{bodyTextA}</span>
            <span className="hide-above-lg">{bodyTextB}</span>
        </span>
    );

    const imageDisplay = css({
        [media.above.lg]: {
            display: "none"
        }
    });

    const tabs: IndividualTabPropsQuote[] = [
        {
            textLabel: "Plan",
            isForceDisplayTextLabelOnPill: true,
            id: "tab-plan",
            headerText: PlanTabHeader({
                headerTextA: intl.formatMessage(
                    {
                        id: "JiraSoftwarePageTemplate.tabsHeader.plan.jswBoardShown",
                        defaultMessage: "Create your{br}first issue"
                    },
                    DEFAULT_VALUES
                ) as string,
                headerTextB: intl.formatMessage(
                    {
                        id: "JiraSoftwarePageTemplate.tabsHeader.plan.jswBoardNotShown",
                        defaultMessage: "Plan"
                    },
                    DEFAULT_VALUES
                ) as string,
                lozengeText: intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.tabsHeader.plan.lozenge",
                    defaultMessage: "try it"
                })
            }),
            bodyText: PlanTabBody({
                bodyTextA: intl.formatMessage(
                    {
                        id: "JiraSoftwarePageTemplate.tabsBody.plan.jswBoardShown",
                        defaultMessage: `Use this interactive kanban board to create an issue, prioritize it, and add it to the board.{br}{br} Kanban boards help teams visualize their workflow to maximize their efficiency.`
                    },
                    DEFAULT_VALUES
                ),
                bodyTextB: intl.formatMessage(
                    {
                        id: "JiraSoftwarePageTemplate.tabsBody.plan.jswBoardNotShown",
                        defaultMessage: `Break the big ideas down into manageable chunks across teams with user stories, issues, and tasks.`
                    },
                    DEFAULT_VALUES
                )
            }),
            // JSW Interactive Board
            display: (
                <>
                    <JswInteractiveBoard {...interactiveBoardProps} />
                    <div css={imageDisplay}>{renderTabContent({ tab })}</div>
                </>
            )
        },
        {
            textLabel: "Track",
            id: "tab-track",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.track",
                defaultMessage: "Track"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.track",
                defaultMessage:
                    "Prioritize and discuss your team’s work in context with complete visibility at every level."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.track",
                defaultMessage:
                    "The pace of delivery from idea to reality has exploded."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.track",
                defaultMessage: "Greg Franklin"
            }),
            company: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsCompany.track",
                defaultMessage: "Agile lead, Cancer Research UK"
            }),
            display: {
                imageProps: {
                    src: trackLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.track",
                        defaultMessage:
                            "The timeline view in Jira allows users to review progress by sprints in weeks, months, or quarters. Status is by category in this example."
                    })
                }
            }
        },
        {
            textLabel: "Release",
            id: "tab-release",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.release",
                defaultMessage: "Release"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.release",
                defaultMessage:
                    "Ship faster, with confidence knowing the information you have is always up-to-date."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.release",
                defaultMessage:
                    "Less friction in the development process means better results and a faster time to market."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.release",
                defaultMessage: "Louis Bennett"
            }),
            company: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsCompany.release",
                defaultMessage: "Director of Engineering, Trulia"
            }),
            display: {
                imageProps: {
                    src: releaseLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.release",
                        defaultMessage:
                            "Releases within a Jira project. This view contains the version, status, progress, start date, release date, and description."
                    })
                }
            }
        },
        {
            textLabel: "Report",
            id: "tab-report",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.report",
                defaultMessage: "Report"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.report",
                defaultMessage:
                    "Improve team performance in context, based on real-time, visual data."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.report",
                defaultMessage:
                    "Full visibility in Jira across the whole organization enables you to make better management judgment. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.report",
                defaultMessage: "Paul Leaon"
            }),
            company: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsCompany.report",
                defaultMessage: "Digital Portfolio Director, Inchcape"
            }),
            display: {
                imageProps: {
                    src: reportLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.report",
                        defaultMessage:
                            "Within the Kanban board in Jira, a project backlog shows 5 issues are on the board and 8 are in backlog."
                    })
                }
            }
        },
        {
            textLabel: "Automate",
            id: "tab-automate",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.automate",
                defaultMessage: "Automate"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.automate",
                defaultMessage:
                    "Save time, keep your team focused, and help work flow with no-code automation."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.automate",
                defaultMessage:
                    "We’re saving 150 hours+ of valuable engineering time per month. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.automate",
                defaultMessage: "Jeff Lai"
            }),
            company: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsCompany.automate",
                defaultMessage: "Internal Infrastructure, canva"
            }),
            display: {
                imageProps: {
                    src: automateLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.automate",
                        defaultMessage:
                            "Automation workflow in Jira. An example automation closes all stories in an epic when the epic is completed."
                    })
                }
            }
        }
    ];

    return (
        <section
            css={css({
                // Selectively show/hide the plan tab body text based on screen size
                ".hide-below-lg": {
                    [media.below.md]: {
                        display: "none"
                    }
                },
                ".hide-above-lg": {
                    [media.above.md]: {
                        display: "none"
                    }
                },
                ".flex-wrapper": {
                    display: "flex",
                    flexFlow: "column nowrap",
                    justifyItems: "flex-start"
                },
                ".lozenge-wrapper": {
                    transform: "scale(1.5)",
                    width: "fit-content",
                    marginInlineStart: "12px",
                    marginBlockEnd: "8px",
                    [media.below.md]: {
                        display: "none"
                    }
                },
                "letterSpacing": "0.3px",
                ".software-jira-tabs": {
                    backgroundImage:
                        "linear-gradient( 123.5deg, #091e42 78%, #0747a6 99.98% )",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover"
                },
                ".tab-group-inner": {
                    gap: "8px",
                    button: {
                        "padding": "4px 16px",
                        "border": "1px solid transparent",
                        "transition": "all 0.25s",
                        "&:hover": {
                            border: "1px solid #c1c7d0"
                        },
                        "&:focus-visible": {
                            outline: "revert"
                        },
                        [media.below.md]: {
                            fontSize: "1.25rem"
                        }
                    },
                    [`[aria-selected="true"]`]: {
                        "&:hover": {
                            borderColor: "transparent"
                        }
                    }
                },
                ".select-container": {
                    ".row-layout": {
                        padding: 0
                    }
                },
                ".select-container-inner": {
                    minWidth: "300px",
                    maxWidth: "388px",
                    width: "100%",
                    margin: "auto"
                },
                "#tabs-section": {
                    "border": "none",
                    "fontSize": "1rem",
                    "lineHeight": "40px",
                    "padding": "0 15px",
                    "width": "100%",
                    "&:focus-visible": {
                        outline: "revert"
                    }
                },
                ".content-block": {
                    width: "100%"
                },
                ".tab-quote-image": {
                    width: "45px"
                },
                ".tab-panel": {
                    "padding": "16px",
                    ".row-layout": {
                        alignItems: "flex-start",
                        padding: 0,
                        maxWidth: "1440px",
                        width: "100%",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 3fr)",
                        [media.only.md]: {
                            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)"
                        },
                        [media.below.sm]: {
                            display: "flex",
                            flexDirection: "column-reverse",
                            gridTemplateColumns: "minmax(0, 1fr)"
                        }
                    }
                },
                ".text-section": {
                    h2: {
                        textAlign: "center",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontSize: "2.25rem",
                        margin: "0"
                    },
                    h3: {
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontSize: "2.25rem",
                        margin: 0,
                        marginBlockEnd: "24px"
                    },
                    p: {
                        fontFamily: "var(--bxp-charlie-text-font-family)",
                        marginBlockEnd: "24px",
                        fontSize: "1.25rem",
                        fontWeight: "normal",
                        letterSpacing: "0.3px",
                        lineHeight: 1.4
                    },
                    [media.below.sm]: {
                        "h2, h3": {
                            fontSize: "1.5rem"
                        }
                    }
                },
                ":last-child": {
                    [media.below.md]: {
                        display: "none"
                    }
                },
                ".quote-wrapper": {
                    borderRadius: "0px 150px 150px 0px",
                    [media.below.md]: {
                        display: "none"
                    }
                },
                ".quote-blurb": {
                    fontSize: "1.25rem",
                    lineHeight: 1.2
                },
                ".quote-name": {
                    fontSize: "0.875rem",
                    lineHeight: 1.25,
                    width: "75%"
                },
                ".quote-company": {
                    fontSize: "0.75rem !important",
                    lineHeight: 2,
                    width: "75%"
                },
                "p": {
                    fontSize: "1.25rem",
                    lineHeight: 1.2
                }
            })}
        >
            <TabsQuote
                id="tabs-section"
                label="Tabs"
                headerText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.tabsHeader",
                    defaultMessage: "All from a single source of truth"
                })}
                bodyText=""
                tabs={tabs}
                colors={{
                    defaultBG: "n600",
                    defaultText: "n60",
                    activeBG: "g200",
                    activeText: "n900",
                    hoverBG: "n600",
                    hoverText: "n60",
                    quoteColor: "b300"
                }}
                className="software-jira-tabs"
            />
        </section>
    );
};

export default TabsSection;
